import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import ReceiptModal from '../components/ReceiptModal';
import { Calendar, User, CreditCard, FileText, Smartphone, ExternalLink, Share, PlusSquare, NotebookPen, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('bookings');
  const [bookingFilter, setBookingFilter] = useState('upcoming');  // 'upcoming', 'past', 'all'
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    phone: ''
  });
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [myNotes, setMyNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        phone: user.phone || ''
      });
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === 'bookings' && user) {
      fetchBookings();
    }
    if (activeTab === 'notes' && user) {
      fetchMyNotes();
    }
  }, [activeTab, user, bookingFilter]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/dashboard/bookings?filter=${bookingFilter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error(t('Failed to load bookings'));
    } finally {
      setLoading(false);
    }
  };

  const fetchMyNotes = async () => {
    setNotesLoading(true);
    try {
      const response = await axios.get(`${API}/my-lesson-notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyNotes(response.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
      // Don't show error if user just doesn't have notes
      setMyNotes([]);
    } finally {
      setNotesLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm(t('Are you sure you want to cancel this booking?'))) {
      return;
    }

    try {
      await axios.delete(`${API}/dashboard/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('Booking cancelled successfully'));
      fetchBookings(); // Refresh list
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error(error.response?.data?.detail || t('Failed to cancel booking'));
    }
  };

  const handleViewReceipt = async (bookingId) => {
    try {
      const response = await axios.get(`${API}/dashboard/bookings/${bookingId}/receipt`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceiptData(response.data);
      setShowReceiptModal(true);
    } catch (error) {
      console.error('Error fetching receipt:', error);
      toast.error(error.response?.data?.detail || t('Failed to load receipt'));
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(
        `${API}/dashboard/profile`,
        null,
        {
          params: profileData,
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success(t('Profile updated successfully'));
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('Failed to update profile'));
    } finally {
      setLoading(false);
    }
  };

  const openStripePortal = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/dashboard/stripe-portal`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error opening Stripe portal:', error);
      toast.error(error.response?.data?.detail || 'Failed to open payment settings');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div>
        <Navbar />
        <div className="booking-page" style={{ paddingTop: '100px', textAlign: 'center' }}>
          <p>{t('Loading...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="booking-page" style={{ paddingTop: '100px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
          <h1 className="booking-title" style={{ marginBottom: '0.5rem' }}>
            {t('Welcome back')}, {user.name}!
          </h1>
          <p style={{ color: '#666', marginBottom: '1rem' }}>{user.email}</p>

          {/* Approval Status Banner - Show if user is NOT approved */}
          {user.approved === false && (
            <div 
              data-testid="approval-pending-banner"
              style={{
                background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                border: '2px solid #ff9800',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '2rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1rem'
              }}
            >
              <div style={{ 
                background: '#ff9800', 
                borderRadius: '50%', 
                padding: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <User size={24} color="white" />
              </div>
              <div>
                <h3 style={{ margin: '0 0 0.5rem 0', color: '#e65100', fontSize: '1.1rem' }}>
                  {t('Account Awaiting Approval')}
                </h3>
                <p style={{ margin: 0, color: '#5d4037', lineHeight: '1.5' }}>
                  {t('Your account is currently awaiting admin approval. You will be able to book lessons once your account has been approved. We will notify you by email when your account is ready.')}
                </p>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div style={{ 
            display: 'flex', 
            gap: '1rem', 
            borderBottom: '2px solid #e5e5e5',
            marginBottom: '2rem'
          }}>
            <button
              onClick={() => setActiveTab('bookings')}
              style={{
                background: 'none',
                border: 'none',
                padding: '1rem',
                cursor: 'pointer',
                color: activeTab === 'bookings' ? '#7c6f5e' : '#666',
                borderBottom: activeTab === 'bookings' ? '2px solid #7c6f5e' : 'none',
                marginBottom: '-2px',
                fontWeight: activeTab === 'bookings' ? 600 : 400,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Calendar size={20} />
              {t('My Bookings')}
            </button>
            <button
              onClick={() => setActiveTab('profile')}
              style={{
                background: 'none',
                border: 'none',
                padding: '1rem',
                cursor: 'pointer',
                color: activeTab === 'profile' ? '#7c6f5e' : '#666',
                borderBottom: activeTab === 'profile' ? '2px solid #7c6f5e' : 'none',
                marginBottom: '-2px',
                fontWeight: activeTab === 'profile' ? 600 : 400,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <User size={20} />
              {t('Profile')}
            </button>
            <button
              onClick={() => setActiveTab('app')}
              style={{
                background: 'none',
                border: 'none',
                padding: '1rem',
                cursor: 'pointer',
                color: activeTab === 'app' ? '#7c6f5e' : '#666',
                borderBottom: activeTab === 'app' ? '2px solid #7c6f5e' : 'none',
                marginBottom: '-2px',
                fontWeight: activeTab === 'app' ? 600 : 400,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Smartphone size={20} />
              {t('Get as App')}
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              style={{
                background: 'none',
                border: 'none',
                padding: '1rem',
                cursor: 'pointer',
                color: activeTab === 'notes' ? '#6b5e8e' : '#666',
                borderBottom: activeTab === 'notes' ? '2px solid #6b5e8e' : 'none',
                marginBottom: '-2px',
                fontWeight: activeTab === 'notes' ? 600 : 400,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <NotebookPen size={20} />
              {t('Mine Noter')}
            </button>
            {/* Payment Methods tab - TEMPORARILY HIDDEN (Stripe disabled)
            <button
              onClick={() => setActiveTab('payment')}
              style={{
                background: 'none',
                border: 'none',
                padding: '1rem',
                cursor: 'pointer',
                color: activeTab === 'payment' ? '#7c6f5e' : '#666',
                borderBottom: activeTab === 'payment' ? '2px solid #7c6f5e' : 'none',
                marginBottom: '-2px',
                fontWeight: activeTab === 'payment' ? 600 : 400,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <CreditCard size={20} />
              Payment Methods
            </button>
            */}
          </div>

          {/* Tab Content */}
          {activeTab === 'bookings' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '1.5rem' }}>
                {/* Filter buttons */}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => setBookingFilter('upcoming')}
                    style={{
                      padding: '0.5rem 1rem',
                      background: bookingFilter === 'upcoming' ? '#7c6f5e' : '#f5f5f5',
                      color: bookingFilter === 'upcoming' ? 'white' : '#666',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: bookingFilter === 'upcoming' ? 500 : 400
                    }}
                  >
                    {t('Upcoming')}
                  </button>
                  <button
                    onClick={() => setBookingFilter('past')}
                    style={{
                      padding: '0.5rem 1rem',
                      background: bookingFilter === 'past' ? '#7c6f5e' : '#f5f5f5',
                      color: bookingFilter === 'past' ? 'white' : '#666',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: bookingFilter === 'past' ? 500 : 400
                    }}
                  >
                    {t('Past')}
                  </button>
                  <button
                    onClick={() => setBookingFilter('all')}
                    style={{
                      padding: '0.5rem 1rem',
                      background: bookingFilter === 'all' ? '#7c6f5e' : '#f5f5f5',
                      color: bookingFilter === 'all' ? 'white' : '#666',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: bookingFilter === 'all' ? 500 : 400
                    }}
                  >
                    {t('All')}
                  </button>
                </div>
              </div>
              
              {loading ? (
                <p>{t('Loading bookings...')}</p>
              ) : bookings.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '3rem', 
                  background: '#f5f5f5', 
                  borderRadius: '10px' 
                }}>
                  <p style={{ color: '#666', marginBottom: '1rem' }}>
                    {bookingFilter === 'upcoming' 
                      ? t("You don't have any upcoming bookings.")
                      : bookingFilter === 'past'
                      ? t("You don't have any past bookings yet.")
                      : t("You don't have any bookings yet.")}
                  </p>
                  <button
                    className="cta-button"
                    onClick={() => navigate('/')}
                  >
                    {t('Book a Lesson')}
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {bookings.map((booking) => {
                    const bookingDate = new Date(booking.date + 'T00:00:00');
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const isUpcoming = bookingDate >= today;
                    const isCancelled = booking.status === 'cancelled';
                    
                    return (
                      <div
                        key={booking.id}
                        style={{
                          border: '1px solid #e5e5e5',
                          borderRadius: '10px',
                          padding: '1.5rem',
                          background: isCancelled ? '#fafafa' : 'white',
                          opacity: isCancelled ? 0.7 : 1
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                              <h3 style={{ fontSize: '1.2rem', margin: 0 }}>
                                {booking.instructor_name}
                              </h3>
                              {isCancelled && (
                                <span style={{
                                  padding: '0.25rem 0.75rem',
                                  borderRadius: '12px',
                                  background: '#ffebee',
                                  color: '#c62828',
                                  fontSize: '0.75rem',
                                  fontWeight: 500
                                }}>
                                  {t('Cancelled')}
                                </span>
                              )}
                            </div>
                            <p style={{ color: '#666', marginBottom: '0.3rem', fontSize: '0.95rem' }}>
                              📅 {bookingDate.toLocaleDateString('da-DK', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                            </p>
                            <p style={{ color: '#666', marginBottom: '0.3rem', fontSize: '0.95rem' }}>
                              🕐 {booking.start_time} ({booking.duration_minutes} min)
                            </p>
                            <p style={{ color: '#666', fontSize: '0.95rem' }}>
                              📍 {booking.booking_type === 'online' ? 'Pivo Online' : 
                                  booking.booking_type === 'greven' ? 'JCP Teaching, Heiden' : 
                                  booking.booking_type === 'lesson' ? 'JCP Teaching' : booking.booking_type}
                            </p>
                            {booking.price > 0 && (
                              <p style={{ color: '#666', marginTop: '0.5rem', fontSize: '0.95rem' }}>
                                💰 €{booking.price}
                              </p>
                            )}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end' }}>
                            <span style={{
                              padding: '0.4rem 1rem',
                              borderRadius: '20px',
                              background: booking.payment_method === 'online' 
                                ? (booking.payment_status === 'paid' ? '#4CAF50' : '#fff3e0')
                                : '#e3f2fd',
                              color: booking.payment_method === 'online'
                                ? (booking.payment_status === 'paid' ? 'white' : '#f57c00')
                                : '#1976d2',
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}>
                              {booking.payment_method === 'online' 
                                ? (booking.payment_status === 'paid' ? `✓ ${t('Paid')}` : t('Payment Pending'))
                                : t('Pay on arrival')}
                            </span>
                            {booking.payment_status === 'paid' && (
                              <button
                                onClick={() => handleViewReceipt(booking.id)}
                                style={{
                                  padding: '0.4rem 1rem',
                                  background: '#7c6f5e',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '5px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem'
                                }}
                              >
                                <FileText size={16} />
                                {t('View Receipt')}
                              </button>
                            )}
                            {isUpcoming && !isCancelled && (
                              <button
                                onClick={() => handleCancelBooking(booking.id)}
                                style={{
                                  padding: '0.4rem 1rem',
                                  background: '#f44336',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '5px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem'
                                }}
                              >
                                {t('Cancel Booking')}
                              </button>
                            )}
                          </div>
                        </div>
                        
                        {/* Payment Info for Pivo Online bookings with pending payment */}
                        {booking.booking_type === 'online' && booking.payment_status !== 'paid' && !isCancelled && (
                          <div style={{
                            marginTop: '1rem',
                            padding: '1rem',
                            background: '#fff8e1',
                            borderRadius: '8px',
                            border: '1px solid #ffe082'
                          }}>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '0.5rem', 
                              marginBottom: '0.75rem',
                              fontWeight: 600,
                              color: '#f57c00'
                            }}>
                              <CreditCard size={18} />
                              {t('Payment Information')}
                            </div>
                            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.75rem' }}>
                              {t('Please transfer the amount before your lesson. Use your name as payment reference.')}
                            </p>
                            <div style={{ 
                              background: 'white', 
                              padding: '0.75rem', 
                              borderRadius: '6px',
                              fontSize: '0.9rem'
                            }}>
                              <div style={{ marginBottom: '0.5rem' }}>
                                <span style={{ color: '#888', width: '50px', display: 'inline-block' }}>IBAN:</span>
                                <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>DE02 4006 9408 0429 2960 01</span>
                              </div>
                              <div style={{ marginBottom: '0.5rem' }}>
                                <span style={{ color: '#888', width: '50px', display: 'inline-block' }}>BIC:</span>
                                <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>GENODEM1BAU</span>
                              </div>
                              <div>
                                <span style={{ color: '#888', width: '50px', display: 'inline-block' }}>Name:</span>
                                <span style={{ fontWeight: 500 }}>Julie Christiansen</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'profile' && (
            <div>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>{t('Profile Settings')}</h2>
              <form onSubmit={handleProfileUpdate} style={{ maxWidth: '500px' }}>
                <div className="form-group">
                  <label className="form-label">{t('Email')}</label>
                  <input
                    type="email"
                    className="form-input"
                    value={user.email}
                    disabled
                    style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                  <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
                    {t('Email cannot be changed')}
                  </p>
                </div>

                <div className="form-group">
                  <label className="form-label">{t('Name')}</label>
                  <input
                    type="text"
                    className="form-input"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{t('Phone')}</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  />
                </div>

                <button
                  type="submit"
                  className="cta-button"
                  disabled={loading}
                  style={{ marginTop: '1rem' }}
                >
                  {loading ? t('Saving...') : t('Save Changes')}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'app' && (
            <div>
              <div style={{ 
                background: 'white', 
                borderRadius: '16px', 
                padding: '2rem',
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                maxWidth: '600px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '1rem', 
                  marginBottom: '1.5rem'
                }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
                    borderRadius: '16px',
                    padding: '1rem',
                    color: 'white'
                  }}>
                    <Smartphone size={32} />
                  </div>
                  <div>
                    <h2 style={{ margin: 0, fontSize: '1.5rem', color: '#333' }}>
                      {t('Get as App')}
                    </h2>
                    <p style={{ margin: '0.25rem 0 0 0', color: '#666', fontSize: '0.95rem' }}>
                      {t('Add JCP Lessons to your home screen')}
                    </p>
                  </div>
                </div>

                <p style={{ 
                  color: '#555', 
                  fontSize: '1rem', 
                  lineHeight: '1.6',
                  marginBottom: '1.5rem'
                }}>
                  {t('app_description')}
                </p>

                {/* iPhone Instructions */}
                <div style={{
                  background: '#f8f9fa',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  marginBottom: '1.5rem'
                }}>
                  <h3 style={{ 
                    margin: '0 0 1rem 0', 
                    fontSize: '1.1rem', 
                    color: '#333',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span style={{ fontSize: '1.25rem' }}>📱</span> {t('How to install on iPhone')}
                  </h3>
                  
                  <ol style={{ 
                    margin: 0, 
                    paddingLeft: '1.25rem',
                    color: '#555',
                    lineHeight: '2'
                  }}>
                    <li style={{ marginBottom: '0.5rem' }}>
                      <strong>{t('Step 1')}:</strong> {t('Open the website in Safari on your iPhone')}
                    </li>
                    <li style={{ marginBottom: '0.5rem' }}>
                      <strong>{t('Step 2')}:</strong> {t('Tap the Share button')} 
                      <span style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: '0.25rem',
                        background: '#e3e8ed',
                        padding: '0.15rem 0.5rem',
                        borderRadius: '4px',
                        marginLeft: '0.5rem',
                        fontSize: '0.85rem'
                      }}>
                        <Share size={14} /> 
                      </span>
                    </li>
                    <li style={{ marginBottom: '0.5rem' }}>
                      <strong>{t('Step 3')}:</strong> {t('Select "Add to Home Screen"')}
                      <span style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: '0.25rem',
                        background: '#e3e8ed',
                        padding: '0.15rem 0.5rem',
                        borderRadius: '4px',
                        marginLeft: '0.5rem',
                        fontSize: '0.85rem'
                      }}>
                        <PlusSquare size={14} />
                      </span>
                    </li>
                    <li>
                      <strong>{t('Step 4')}:</strong> {t('The icon will appear with "JCP LESSONS" text')}
                    </li>
                  </ol>
                </div>

                {/* Link section */}
                <p style={{ 
                  margin: '0 0 1rem 0', 
                  fontSize: '0.95rem',
                  color: '#555',
                  textAlign: 'center'
                }}>
                  {t('Tap the link and follow the 4 steps above')}:
                </p>
                
                <a 
                  href="https://booking-admin-14.emergent.host/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.75rem',
                    width: '100%',
                    background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
                    color: 'white',
                    padding: '1rem 1.5rem',
                    borderRadius: '12px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <ExternalLink size={20} />
                  booking-admin-14.emergent.host
                </a>

                {/* Android note */}
                <p style={{ 
                  marginTop: '1.5rem', 
                  color: '#888', 
                  fontSize: '0.85rem',
                  fontStyle: 'italic'
                }}>
                  💡 {t('Android users can also add to home screen via Chrome menu')}
                </p>
              </div>
            </div>
          )}

          {/* Payment Methods tab content - TEMPORARILY HIDDEN (Stripe disabled)
          {activeTab === 'payment' && (
            <div>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Payment Methods</h2>
              <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                Manage your saved payment methods through Stripe's secure portal.
              </p>
              
              <button
                className="cta-button"
                onClick={openStripePortal}
                disabled={loading}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <CreditCard size={20} />
                {loading ? 'Opening...' : 'Manage Payment Methods'}
              </button>
            </div>
          )}
          */}

          {/* My Notes Tab */}
          {activeTab === 'notes' && (
            <div>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#6b5e8e' }}>
                📝 {t('Mine Undervisningsnoter')}
              </h2>
              <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                {t('Her kan du se noter fra dine undervisningstimer.')}
              </p>

              {notesLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                  {t('Indlæser noter...')}
                </div>
              ) : myNotes.length === 0 ? (
                <div style={{ 
                  background: '#f9f9f9', 
                  borderRadius: '12px', 
                  padding: '3rem', 
                  textAlign: 'center',
                  color: '#666'
                }}>
                  <NotebookPen size={48} style={{ marginBottom: '1rem', color: '#ccc' }} />
                  <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                    {t('Ingen noter endnu')}
                  </p>
                  <p style={{ fontSize: '0.9rem' }}>
                    {t('Når din instruktør frigiver noter fra undervisningen, vil de blive vist her.')}
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {myNotes.map(note => (
                    <div 
                      key={note.id} 
                      style={{ 
                        background: 'white', 
                        borderRadius: '12px', 
                        padding: '1.25rem',
                        border: '1px solid #e0e0e0',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                      }}
                    >
                      {/* Note Header */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.75rem', 
                        marginBottom: '1rem',
                        paddingBottom: '0.75rem',
                        borderBottom: '1px solid #f0f0f0'
                      }}>
                        <div style={{
                          background: 'linear-gradient(135deg, #6b5e8e 0%, #8a7aad 100%)',
                          borderRadius: '8px',
                          padding: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <span style={{ fontSize: '1.25rem' }}>🐴</span>
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: '#333' }}>
                            {note.horse_name || t('Undervisning')}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#999', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Clock size={14} />
                            {new Date(note.lesson_date).toLocaleDateString('da-DK', {
                              weekday: 'long',
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Worked On */}
                      {note.worked_on && note.worked_on.length > 0 && (
                        <div style={{ marginBottom: '1rem' }}>
                          <h4 style={{ 
                            margin: '0 0 0.5rem 0', 
                            fontSize: '0.9rem', 
                            color: '#7c6f5e',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            ✅ {t('Vi arbejdede med')}:
                          </h4>
                          <ul style={{ 
                            margin: 0, 
                            paddingLeft: '1.25rem', 
                            color: '#333',
                            fontSize: '0.95rem'
                          }}>
                            {note.worked_on.map((item, i) => (
                              <li key={i} style={{ marginBottom: '0.35rem' }}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Homework */}
                      {note.homework && note.homework.length > 0 && (
                        <div style={{
                          background: '#f8f7fc',
                          borderRadius: '8px',
                          padding: '1rem'
                        }}>
                          <h4 style={{ 
                            margin: '0 0 0.5rem 0', 
                            fontSize: '0.9rem', 
                            color: '#6b5e8e',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            🎯 {t('Hjemmearbejde')}:
                          </h4>
                          <ul style={{ 
                            margin: 0, 
                            paddingLeft: '1.25rem', 
                            color: '#333',
                            fontSize: '0.95rem'
                          }}>
                            {note.homework.map((item, i) => (
                              <li key={i} style={{ marginBottom: '0.35rem' }}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Receipt Modal */}
      {showReceiptModal && receiptData && (
        <ReceiptModal
          receiptData={receiptData}
          onClose={() => {
            setShowReceiptModal(false);
            setReceiptData(null);
          }}
        />
      )}
    </div>
  );
};

export default DashboardPage;
